import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { FormsV1 } from '@frontend/api-digital-forms';
import { useTranslation } from '@frontend/i18n';
import { useCountdown } from '@frontend/timer';
import {
  Button,
  DatePickerField,
  Heading,
  InlineNotification,
  Text,
  TextField,
  useFormField,
  useModalControl,
} from '@frontend/design-system';
import { trackDraftEvent } from '../../../../draft/draft-telemetry';
import type { FetchDraftResult, VerifyDraftOwnerResult } from '../../../../hooks/api-hooks/use-draft-api';
import Footer from '../../../layout/footer.component';
import { CountDownTimer } from '../form-components/countdown-timer';
import { containerCardStyles } from '../patron-form-welcome-screen.styles';
import { DiscardDraftModal } from './discard-draft-modal';
import {
  actionsStyles,
  fieldsStyles,
  fullWidthFieldStyles,
  noticeContentStyles,
  noticeWrapperStyles,
  notificationStyles,
} from './resume-draft-screen.styles';

type DraftOutput = FormsV1.Types.GetDraftIO['output'];

interface Props {
  verifyOwner: (args: { mobilePhone: string; birthdate: string }) => Promise<VerifyDraftOwnerResult>;
  fetchDraft: (verificationCode: string) => Promise<FetchDraftResult>;
  /** Applies a retrieved draft to the form. Resolves `false` when it can't be restored for this form. */
  onRestore: (draft: DraftOutput) => Promise<boolean>;
  /** Deletes the draft and continues to a fresh form. Resolves `false` when deletion failed. */
  onDiscard: () => Promise<boolean>;
}

type Phase = 'idle' | 'verifying' | 'restoring';

type Notice =
  | 'mismatch'
  | 'rate-limited'
  | 'verify-error'
  | 'verification-expired'
  | 'retrieval-error'
  | 'unavailable'
  | 'discard-error';

const PHONE_DIGITS = 4;

/** Converts the picker's `MM/DD/YYYY` (or `YYYY-MM-DD`) value to a calendar `YYYY-MM-DD` without timezone math. */
export const toCalendarDate = (value: string) => {
  const us = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (us) return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
  const iso = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`;
  return '';
};

/** Identity check before a saved draft is loaded (spec §5.3). No saved answers are shown before verification. */
export const ResumeDraftScreen = ({ verifyOwner, fetchDraft, onRestore, onDiscard }: Props) => {
  const { t } = useTranslation('digital-forms-experience');
  const [phase, setPhase] = useState<Phase>('idle');
  const [notice, setNotice] = useState<Notice | null>(null);
  const [cooldown, setCooldown] = useState({ seconds: 0, key: 0 });
  /** Kept after a successful verification so a failed retrieval can be retried without re-verifying. */
  const verificationCodeRef = useRef<string | null>(null);
  const { modalProps: discardModalProps, openModal: openDiscardModal } = useModalControl();

  const countdown = useCountdown({
    initialValue: cooldown.seconds,
    onComplete: () => setNotice((current) => (current === 'rate-limited' ? null : current)),
  });
  const { restart: restartCountdown } = countdown;
  const isCoolingDown = countdown.isRunning && countdown.count > 0;

  useEffect(() => {
    if (cooldown.seconds > 0) restartCountdown();
  }, [cooldown, restartCountdown]);

  const phoneField = useFormField({
    type: 'text',
    required: true,
    transformValue: (value) => (value ?? '').replace(/\D/g, '').slice(0, PHONE_DIGITS),
    validator: ({ value }) =>
      new RegExp(`^\\d{${PHONE_DIGITS}}$`).test(value ?? '') ? '' : t('Enter the last 4 digits of your phone number'),
  });

  const birthdateField = useFormField({
    type: 'datePicker',
    required: true,
    maxDate: dayjs().format('MM/DD/YYYY'),
  });

  // New identity input invalidates any earlier verification.
  useEffect(() => {
    verificationCodeRef.current = null;
  }, [phoneField.value, birthdateField.value]);

  const isBusy = phase !== 'idle';

  const retrieve = async (verificationCode: string) => {
    setPhase('restoring');
    const result = await fetchDraft(verificationCode);

    if (result.type === 'error') {
      trackDraftEvent('Draft retrieval failed', { category: result.category }, 'warning');
      if (result.category === 'not-found') {
        setNotice('unavailable');
      } else if (result.category === 'unauthorized' || result.category === 'forbidden') {
        verificationCodeRef.current = null;
        setNotice('verification-expired');
      } else {
        setNotice('retrieval-error');
      }
      setPhase('idle');
      return;
    }

    let restored = false;
    try {
      restored = await onRestore(result.draft);
    } catch {
      setNotice('retrieval-error');
      setPhase('idle');
      return;
    }
    // On success the parent leaves this screen; only failures need local state.
    if (!restored) {
      setNotice('unavailable');
      setPhase('idle');
    }
  };

  const handleResume = async () => {
    if (isBusy || isCoolingDown) return;
    setNotice(null);

    if (verificationCodeRef.current) {
      await retrieve(verificationCodeRef.current);
      return;
    }

    const birthdate = toCalendarDate(birthdateField.value ?? '');
    if (phoneField.error || birthdateField.error || !birthdate) {
      phoneField.onBlur();
      birthdateField.onBlur();
      document.getElementById(phoneField.error ? phoneField.id : birthdateField.id)?.focus();
      return;
    }

    setPhase('verifying');
    const result = await verifyOwner({ mobilePhone: phoneField.value, birthdate });
    trackDraftEvent(
      'Draft verification',
      { outcome: result.type, category: result.type === 'error' ? result.category : undefined },
      result.type === 'verified' ? 'info' : 'warning'
    );

    switch (result.type) {
      case 'verified':
        verificationCodeRef.current = result.verificationCode;
        await retrieve(result.verificationCode);
        return;
      case 'rate-limited':
        setNotice('rate-limited');
        setCooldown((current) => ({ seconds: result.cooldownSeconds, key: current.key + 1 }));
        break;
      case 'mismatch':
        setNotice('mismatch');
        break;
      case 'error':
        setNotice('verify-error');
        break;
    }
    setPhase('idle');
  };

  const handleDiscard = async () => {
    const discarded = await onDiscard();
    trackDraftEvent('Draft discard', { outcome: discarded ? 'success' : 'failure' }, discarded ? 'info' : 'warning');
    if (!discarded) setNotice('discard-error');
    return discarded;
  };

  const resumeLabel =
    phase === 'verifying' ? t('Verifying…') : phase === 'restoring' ? t('Restoring your form…') : t('Resume form');

  const renderNotice = () => {
    switch (notice) {
      case 'mismatch':
        return (
          <InlineNotification
            status='error'
            message={t('Verification details are incorrect. Please check and try again.')}
            maxWidth='100%'
            css={notificationStyles}
          />
        );
      case 'rate-limited':
        return (
          <InlineNotification status='error' maxWidth='100%' css={notificationStyles}>
            <div css={noticeContentStyles}>
              <Text>
                {isCoolingDown
                  ? t('Too many attempts. Please wait before trying again.')
                  : t('Too many attempts. Please try again later.')}
              </Text>
              {isCoolingDown && <CountDownTimer timeInSeconds={countdown.count} />}
            </div>
          </InlineNotification>
        );
      case 'verify-error':
        return (
          <InlineNotification
            status='error'
            maxWidth='100%'
            css={notificationStyles}
            message={t("We couldn't verify your details right now. Please try again.")}
          />
        );
      case 'verification-expired':
        return (
          <InlineNotification
            status='warn'
            maxWidth='100%'
            css={notificationStyles}
            message={t('Your verification expired. Please verify again.')}
          />
        );
      case 'retrieval-error':
        return (
          <InlineNotification
            status='error'
            maxWidth='100%'
            css={notificationStyles}
            message={t("We couldn't load your saved progress. Try again.")}
          />
        );
      case 'unavailable':
        return (
          <InlineNotification
            status='warn'
            maxWidth='100%'
            css={notificationStyles}
            message={t('Your saved progress is no longer available. Select Start fresh to fill out the form.')}
          />
        );
      case 'discard-error':
        return (
          <InlineNotification
            status='error'
            maxWidth='100%'
            css={notificationStyles}
            message={t("We couldn't discard your saved progress. Your draft is still saved. Please try again.")}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div css={containerCardStyles}>
        <div>
          <Heading as='h1' level={2}>
            {t('Welcome back')}
          </Heading>
          <Text color='subdued'>{t('Verify your identity to resume your saved form.')}</Text>
        </div>
        <div css={fieldsStyles}>
          <TextField
            {...phoneField}
            name='draftPhoneLastFour'
            label={t('Last 4 digits of your registered phone number')}
            type='tel'
            maxLength={PHONE_DIGITS}
            autoComplete='off'
            disabled={isBusy}
          />
          <DatePickerField
            {...birthdateField}
            css={fullWidthFieldStyles}
            name='draftBirthdate'
            label={t('Date of birth')}
            disabled={isBusy}
          />
        </div>

        <div css={noticeWrapperStyles}>{renderNotice()}</div>

        <div css={actionsStyles} aria-live='polite'>
          <Button
            variant='primary'
            size='large'
            onClick={handleResume}
            disabled={isBusy || isCoolingDown || notice === 'unavailable'}
          >
            {resumeLabel}
          </Button>
          <Button variant='secondary' size='large' onClick={openDiscardModal} disabled={isBusy || isCoolingDown}>
            {t('Start fresh')}
          </Button>
        </div>
      </div>
      <Footer />
      <DiscardDraftModal modalProps={discardModalProps} onDiscard={handleDiscard} />
    </>
  );
};
